


import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import './Registration.css';
import LocationPicker from './LocationPicker';
import { useLoader } from "../../../../context/LoaderProvider";
import Modal from './Modal';
import axios from "axios";

const initialRowsConfig = [
  { key: "Owner Name", value: "", isEditing: false, status: "Pending" },
  { key: "Company Name", value: "", isEditing: false, status: "Pending" },
  {
    key: "Business Vertical", value: "", isEditing: false, status: "Pending", isDropdown: true,
    options: ["FOOD_AND_BEVERAGES"]
  },
  {
    key: "Outlet Vertical", value: "", isEditing: false, status: "Pending", isDropdown: true,
    options: ["HOTEL", "RESTAURANT", "CAFE", "CLOUD_KITCHEN", "FOOD_COURT", "STREET_FOOD", "BAKERY"]
  },
  {
    key: "Order Type",
    value: [],
    isEditing: false,
    status: "Pending",
    isMultiSelectDropdown: true,
    options: [], // dynamically updated
  },
  { key: "Register Documents", value: "", document: null, isEditing: false, isUpload: true, status: "Pending" },
  { key: "Website", value: "", isEditing: false, status: "Pending" },
  { key: "Phone No", value: "", isEditing: false, status: "Pending" },
  { key: "Email ID", value: "", isEditing: false, status: "Pending" },
  {
    key: "Address",
    value: {
      doorNo: "",
      street: "",
      city: "",
      state: "",
      pincode: "",
      landmark: "",
      latitude: "",   // ✅ added latitude
      longitude: ""   // ✅ added longitude
    },
    isEditing: false,
    status: "Pending",
    isMapPicker: true   // ✅ flag to show "Add Location" button
  },
  { key: "AADHAR CARD NO", value: "", document: null, isUpload: true, isEditing: false, status: "Not Uploaded" },
  { key: "PAN CARD NO", value: "", document: null, isUpload: true, isEditing: false, status: "Not Uploaded" },
  { key: "Trade License", value: { startDate: "", endDate: "", licenseNumber: "" }, document: null, isUpload: true, isEditing: false, status: "Not Uploaded" },
  {
    key: "FSC License",
    value: {
      startDate: "",
      endDate: "",
      licenseNumber: ""  // new field for number input
    },
    document: null,
    isUpload: true,
    isEditing: false,
    status: "Not Uploaded"
  },
  { key: "Labour License", value: { startDate: "", endDate: "", licenseNumber: "" }, document: null, isUpload: true, isEditing: false, status: "Not Uploaded" },
  { key: "GST No.", value: "", isEditing: false, status: "Not Verified" },
  {
    key: "Bank Details",
    value: { holderName: "", accountNumber: "", branchName: "", ifscCode: "", passbookFile: null, passbookServerFileName: "" },
    isEditing: false, status: "Pending"
  },
];

const Registration = ({ onNext }) => {
  const [vendorDetails, setVendorDetails] = useState(null);
  const [rows, setRows] = useState(
    initialRowsConfig.map(r =>
      r.key === "Remarks" ? { ...r, isEditing: false } : r
    )
  );

  const fileInputRefs = useRef([]);
  const nestedFileInputRefs = useRef({});
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vendorId, setVendorId] = useState(null);
  const [currentVendorId, setCurrentVendorId] = useState(null);
  const navigate = useNavigate();
  const tableContainerRef = useRef(null);
  // Add this state near your other states
  const [editingField, setEditingField] = useState(null);

  // ✅ Add state to track if registration is complete
  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);

  const getRowConfigForKey = (key) => initialRowsConfig.find(r => r.key === key) || { value: {} };
  const { showLoader, hideLoader } = useLoader();

  const [showMap, setShowMap] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState({
    doorNo: "",
    street: "",
    city: "",
    state: "",
    pincode: "",
    landmark: "",
    latitude: "",
    longitude: ""
  });
  const [formRows, setFormRows] = useState([
    {
      key: "Address",
      value: selectedAddress,
      isEditing: false,
      status: "Pending",
      isMapPicker: true
    },
  ]);

  const [rowsConfig, setRowsConfig] = useState(initialRowsConfig);
  const [planType, setPlanType] = useState(null);

  // ✅ Helper function to check if a field is editable
  const isFieldEditable = (fieldKey) => {
    if (!isRegistrationComplete) return true; // All fields editable during initial registration
    if (fieldKey === "Address") return true; // Address always editable
    return false; // All other fields locked after registration
  };

  useEffect(() => {
    const vendorId = localStorage.getItem("vendorId");
    const token = localStorage.getItem("authToken");

    if (!vendorId || !token) {
      alert("Vendor ID or Auth Token missing in localStorage");
      return;
    }

    const fetchSubscription = async () => {
      try {
        const apiUrl = `https://backend.maamaas.com/subscription/api/get/vendor_subscription/${vendorId}/FOOD_AND_BEVERAGES`;

        const res = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = res.data?.[0];
        const currentPlanType = data?.subscriptionPlan?.planType || "BASIC";

        setPlanType(currentPlanType);

        const orderTypeOptions =
          currentPlanType === "PREMIUM"
            ? ["DINE_IN", "DELIVERY", "TAKEAWAY", "TABLE_DINE_IN"]
            : ["DINE_IN", "TAKEAWAY"];

        const updatedConfig = initialRowsConfig.map((row) =>
          row.key === "Order Type" ? { ...row, options: orderTypeOptions } : row
        );

        setRowsConfig(updatedConfig);
      } catch (error) {
        console.error("❌ Error fetching subscription:", error);
        if (error.response) {
          console.error("Response data:", error.response.data);
          console.error("Status code:", error.response.status);
        } else if (error.request) {
          console.error("No response received. Request:", error.request);
        } else {
          console.error("Error setting up request:", error.message);
        }
        // alert("Failed to fetch subscription details. Check console for more info.");
      }
    };

    fetchSubscription();
  }, []);

  const handleConfirmLocation = async () => {
    if (!selectedAddress.latitude || !selectedAddress.longitude) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${selectedAddress.latitude}&lon=${selectedAddress.longitude}`
      );
      const data = await response.json();
      const address = data.address || {};

      const updatedAddress = {
        doorNo: address.house_number || "",
        street: address.road || "",
        city: address.city || address.town || address.village || "",
        state: address.state || "",
        pincode: address.postcode || "",
        landmark: address.neighbourhood || address.suburb || "",
        latitude: selectedAddress.latitude,
        longitude: selectedAddress.longitude
      };

      // Update the rows state and set as editing
      setRows(prevRows =>
        prevRows.map(row =>
          row.key === "Address"
            ? { ...row, value: updatedAddress, isEditing: true }
            : row
        )
      );

      // Set editing field to show submit button
      setEditingField("Address");

      setShowMap(false);
    } catch (error) {
      console.error("Error fetching address:", error);
    }
  };
  useEffect(() => {
    // Clear editing field on mount
    setEditingField(null);

    // ... rest of your existing useEffect code
  }, []);

  const getServerDocPlaceholder = useCallback((rowKey, details) => {
    if (!details) return null;
    switch (rowKey) {
      case "AADHAR CARD NO": return details.aadharFront ? { name: "Aadhaar Document" } : null;
      case "PAN CARD NO": return details.panCard ? { name: "PAN Document " } : null;
      case "Register Documents": return details.registeredDocumentsFront ? { name: "Registered Document " } : null;
      case "Trade License": return details.tradeLicense ? { name: "Trade License " } : null;
      case "FSC License": return details.fssaiLicense ? { name: "FSC License " } : null;
      case "Labour License": return details.labourLicense ? { name: "Labour License " } : null;
      case "Passbook": return details.passbook ? { name: "Passbook " } : null;
      default: return null;
    }
  }, []);

  const getStatusForDocumentField = useCallback((key, details) => {
    const initialStatus = getRowConfigForKey(key)?.status || "Pending";
    if (!details) return initialStatus;
    let hasDocOnServer = false; let isVerifiedByApi = false;
    switch (key) {
      case "AADHAR CARD NO": hasDocOnServer = !!details.aadharPhotoFront; isVerifiedByApi = !!details.aadharNumberStatus; return isVerifiedByApi ? "Verified" : (hasDocOnServer || details.aadharNumber ? "Provided" : "Not Verified");
      case "PAN CARD NO": hasDocOnServer = !!details.panCard; isVerifiedByApi = !!details.panCardStatus; return isVerifiedByApi ? "Verified" : (hasDocOnServer || details.panCardNumber ? "Provided" : "Not Verified");
      case "Register Documents": hasDocOnServer = !!details.registeredDocumentsFront; isVerifiedByApi = !!details.registeredDocumentsStatus; return isVerifiedByApi ? "Verified" : (hasDocOnServer ? "Provided" : "Pending");
      case "Trade License": hasDocOnServer = !!details.tradeLicense; isVerifiedByApi = !!details.tradeLicenseStatus; return isVerifiedByApi ? "Verified" : (hasDocOnServer || details.tradeLicenseStartDate ? "Provided" : "Not Verified");
      case "FSC License": hasDocOnServer = !!details.fssaiLicense; isVerifiedByApi = !!details.fssaiLicenseStatus; return isVerifiedByApi ? "Verified" : (hasDocOnServer || details.fssaiStartDate ? "Provided" : "Not Verified");
      case "Labour License": hasDocOnServer = !!details.labourLicense; isVerifiedByApi = !!details.labourLicenseStatus; return isVerifiedByApi ? "Verified" : (hasDocOnServer || details.labourStartDate ? "Provided" : "Not Verified");
      case "Passbook": hasDocOnServer = !!details.passbook; return hasDocOnServer ? "Provided" : "Not Uploaded";
      default: return initialStatus;
    }
  }, []);

  const handleProceed = () => {
    if (typeof onNext === 'function') onNext();
    else console.error("Registration: onNext prop is not a function!");
  };

  const mapAndSetRowsFromVendorDetails = useCallback((details) => {
    if (!details) {
      setRows(initialRowsConfig.map(r => ({ ...r, isEditing: r.key === "Remarks" ? false : r.isEditing, document: r.isUpload ? null : undefined })));
      return;
    }
    const updatedRows = initialRowsConfig.map(rowConfig => {
      const newRow = { ...rowConfig, document: rowConfig.isUpload ? null : undefined, isEditing: rowConfig.key === "Remarks" ? false : false };
      let valueFromApi;
      let statusFromApi = getRowConfigForKey(rowConfig.key)?.status || "Pending";

      switch (rowConfig.key) {
        case "Owner Name": valueFromApi = details.ownerName; if (details.hasOwnProperty('ownerNameStatus')) statusFromApi = details.ownerNameStatus ? "Verified" : (details.ownerName ? "Provided" : "Pending"); break;
        case "Company Name": valueFromApi = details.registeredName; if (details.hasOwnProperty('registeredNameStatus')) statusFromApi = details.registeredNameStatus ? "Verified" : (details.registeredName ? "Provided" : "Pending"); break;
        case "Business Plan": valueFromApi = details.businessPlan; if (details.hasOwnProperty('businessPlanStatus')) statusFromApi = details.businessPlanStatus ? "Verified" : (details.businessPlan ? "Provided" : "Pending"); break;
        case "Type": valueFromApi = details.type; if (details.hasOwnProperty('typeStatus')) statusFromApi = details.typeStatus ? "Verified" : (details.type ? "Provided" : "Pending"); break;
        case "Vendor Type": valueFromApi = details.vendorType; if (details.vendorType) statusFromApi = "Provided"; break;
        case "Outlet Vertical": valueFromApi = details.type; if (details.type) statusFromApi = "Provided"; break;
        case "Business Vertical": valueFromApi = details.businessVertical; if (details.businessVertical) statusFromApi = "Provided"; break;
        case "Order Type":
          if (Array.isArray(details.orderTypes) && details.orderTypes.length > 0) {
            valueFromApi = details.orderTypes.join(", ");
            statusFromApi = "Provided";
          } else {
            valueFromApi = details.orderTypes || "";
            statusFromApi = "Pending";
          }
          break;

        case "Website": valueFromApi = details.websiteName; if (details.hasOwnProperty('websiteNameStatus')) statusFromApi = details.websiteNameStatus ? "Verified" : (details.websiteName ? "Provided" : "Pending"); break;
        case "Phone No": valueFromApi = details.mobileNumber; if (details.hasOwnProperty('mobileNumberStatus')) statusFromApi = details.mobileNumberStatus ? "Verified" : (details.mobileNumber ? "Provided" : "Pending"); break;
        case "Email ID": valueFromApi = details.email; if (details.hasOwnProperty('emailStatus')) statusFromApi = details.emailStatus ? "Verified" : (details.email ? "Provided" : "Pending"); break;

        case "Address": {
          const defaultAddress = getRowConfigForKey("Address").value;
          valueFromApi = { ...defaultAddress, doorNo: details.doorNumber, street: details.addressLine, city: details.city, state: details.state, pincode: details.pincode !== undefined && details.pincode !== null ? String(details.pincode) : "", landmark: details.landMark, location: details.location };
          if (Object.values(valueFromApi).some(v => !!v)) statusFromApi = "Provided"; else statusFromApi = "Pending";
          break;
        }
        case "AADHAR CARD NO": valueFromApi = details.aadharNumber; newRow.document = getServerDocPlaceholder(rowConfig.key, details); statusFromApi = getStatusForDocumentField(rowConfig.key, details); break;
        case "PAN CARD NO": valueFromApi = details.panCardNumber; newRow.document = getServerDocPlaceholder(rowConfig.key, details); statusFromApi = getStatusForDocumentField(rowConfig.key, details); break;
        case "Trade License":
          valueFromApi = {
            startDate: details.tradeLicenseStartDate || "",
            endDate: details.tradeLicenseEndDate || "",
            licenseNumber: details.tradeLicenseNumber || ""
          };
          newRow.document = getServerDocPlaceholder(rowConfig.key, details);
          statusFromApi = getStatusForDocumentField(rowConfig.key, details);
          break;

        case "FSC License":
          valueFromApi = {
            startDate: details.fssaiStartDate || "",
            endDate: details.fssaiEndDate || "",
            licenseNumber: details.fssaiLicenseNumber || ""
          };
          newRow.document = getServerDocPlaceholder(rowConfig.key, details);
          statusFromApi = getStatusForDocumentField(rowConfig.key, details);
          break;

        case "Labour License":
          valueFromApi = {
            startDate: details.labourStartDate || "",
            endDate: details.labourEndDate || "",
            licenseNumber: details.labourLicenseNumber || ""
          };
          newRow.document = getServerDocPlaceholder(rowConfig.key, details);
          statusFromApi = getStatusForDocumentField(rowConfig.key, details);
          break;

        case "GST No.": valueFromApi = details.gstNumber; if (details.hasOwnProperty('gstNumberStatus')) statusFromApi = details.gstNumberStatus ? "Verified" : (details.gstNumber ? "Provided" : "Not Verified"); else if (details.gstNumber) statusFromApi = "Provided"; else statusFromApi = "Not Verified"; break;
        case "Register Documents": newRow.document = getServerDocPlaceholder(rowConfig.key, details); statusFromApi = getStatusForDocumentField(rowConfig.key, details); break;
        case "Bank Details": {
          const defaultBankDetails = getRowConfigForKey("Bank Details").value;
          valueFromApi = { ...defaultBankDetails, holderName: details.holderName, accountNumber: details.accountNumber, branchName: details.branchName, ifscCode: details.ifscCode, passbookFile: null };
          if (details.passbook) { valueFromApi.passbookServerFileName = "Passbook"; } else { valueFromApi.passbookServerFileName = ""; }
          const hasTextData = valueFromApi.holderName || valueFromApi.accountNumber || valueFromApi.branchName || valueFromApi.ifscCode;
          if (hasTextData || valueFromApi.passbookServerFileName) statusFromApi = "Provided"; else statusFromApi = "Pending";
          break;
        }
        default: valueFromApi = rowConfig.value;
      }
      newRow.value = valueFromApi !== undefined && valueFromApi !== null ? valueFromApi : rowConfig.value;
      newRow.status = statusFromApi;
      return newRow;
    });
    setRows(updatedRows);
  }, [getStatusForDocumentField, getServerDocPlaceholder]);

  const getVendorDetails = useCallback(async (vendorIdToFetchParam) => {
    if (!vendorIdToFetchParam) {
      setVendorDetails(null);
      mapAndSetRowsFromVendorDetails(null);
      return;
    }

    const token = localStorage.getItem("authToken");
    setLoading(true);

    try {
      const response = await fetch(
        `https://backend.maamaas.com/food/api/vendors/${vendorIdToFetchParam}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: "Error fetching and parsing error.",
        }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const fetchedVendorDetails = await response.json();

      if (fetchedVendorDetails?.vendorId) {
        localStorage.setItem(
          `vendorData_${fetchedVendorDetails.vendorId}`,
          JSON.stringify(fetchedVendorDetails)
        );
      }

      setVendorDetails(fetchedVendorDetails);
      mapAndSetRowsFromVendorDetails(fetchedVendorDetails);
    } catch (error) {
      console.error("Failed to fetch vendor details:", error.message);
      setVendorDetails(null);
      mapAndSetRowsFromVendorDetails(null);
    } finally {
      setLoading(false);
    }
  }, [mapAndSetRowsFromVendorDetails]);

  useEffect(() => {
    const storedVendorId = localStorage.getItem("vendorId");
    if (storedVendorId) {
      setCurrentVendorId(storedVendorId);
      getVendorDetails(storedVendorId);
      // ✅ Set registration as complete if vendor ID exists
      setIsRegistrationComplete(true);
    } else {
      setCurrentVendorId(null);
      setVendorDetails(null);
      mapAndSetRowsFromVendorDetails(null);
      // ✅ Set registration as incomplete if no vendor ID
      setIsRegistrationComplete(false);
    }
  }, [getVendorDetails, mapAndSetRowsFromVendorDetails]);

  useEffect(() => {
    const storedVendorId = localStorage.getItem("vendorId");
    const token = localStorage.getItem("authToken");

    if (storedVendorId && token) {
      fetch(`https://backend.maamaas.com/food/api/vendors/${storedVendorId}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        }
      })
        .then(response => {
          if (response.status === 404) return null;
          if (!response.ok) return response.json().then(err => {
            throw new Error(err.message || `HTTP error! ${response.status}`);
          });
          return response.json();
        })
        .then(data => {
          if (data && data.vendorId) {
            setCurrentVendorId(data.vendorId);
            getVendorDetails(data.vendorId);
            // ✅ Set registration as complete
            setIsRegistrationComplete(true);

            if (data.token) {
              localStorage.setItem("authToken", data.token);
            }
          } else {
            setCurrentVendorId(null);
            setVendorDetails(null);
            mapAndSetRowsFromVendorDetails(null);
            // ✅ Set registration as incomplete
            setIsRegistrationComplete(false);
          }
        })
        .catch(error => {
          console.error("Error fetching vendor ID by localStorage vendorId:", error);
          setCurrentVendorId(null);
          setVendorDetails(null);
          mapAndSetRowsFromVendorDetails(null);
          // ✅ Set registration as incomplete
          setIsRegistrationComplete(false);
        });
    } else {
      setCurrentVendorId(null);
      setVendorDetails(null);
      mapAndSetRowsFromVendorDetails(null);
      // ✅ Set registration as incomplete
      setIsRegistrationComplete(false);
    }
  }, [getVendorDetails, mapAndSetRowsFromVendorDetails]);

  const handleEdit = (index) => {
    if (rows[index].key === "Remarks") return;
    if (!isFieldEditable(rows[index].key)) return;

    setRows(prevRows => prevRows.map((r, i) => ({
      ...r,
      isEditing: i === index
    })));

    // Track which field is being edited (for showing submit button)
    setEditingField(rows[index].key);
  };

  const handleSave = (index) => {
    // Exit edit mode
    setRows(prevRows => prevRows.map((r, i) =>
      i === index ? { ...r, isEditing: false } : r
    ));

    // Keep editingField state as is (so submit button stays visible)
    // We'll clear it only after successful submission
  };
  const handleSubmitFieldUpdate = async () => {
    if (!editingField) return;

    const editingRowIndex = rows.findIndex(r => r.key === editingField);
    if (editingRowIndex === -1) return;

    const editingRow = rows[editingRowIndex];

    if (editingField === "Address") {
      try {
        showLoader();

        const token = localStorage.getItem("authToken");
        const vendorId = localStorage.getItem("vendorId");

        if (!vendorId || !token) {
          alert("Vendor ID or Auth Token missing");
          return;
        }

        // Prepare the address data - matching your existing vendorData structure
        const addressData = {
          doorNumber: editingRow.value.doorNo || null,
          addressLine: editingRow.value.street || null,
          city: editingRow.value.city || null,
          state: editingRow.value.state || null,
          pincode: editingRow.value.pincode ? parseInt(editingRow.value.pincode, 10) : null,
          landMark: editingRow.value.landmark || null,
          latitude: editingRow.value.latitude || selectedAddress?.latitude || 0,
          longitude: editingRow.value.longitude || selectedAddress?.longitude || 0,
          vendorId: vendorId
        };

        //console.log("📤 Address data:", addressData);

        // Create FormData like your POST request does
        const formData = new FormData();

        // Add vendorData as a JSON blob - JUST LIKE YOUR POST REQUEST
        formData.append(
          "vendorData",
          new Blob([JSON.stringify(addressData)], { type: "application/json" })
        );

        // Log FormData entries for debugging
       

        const apiUrl = `https://backend.maamaas.com/food/api/vendors/edit/${vendorId}`;

        const response = await fetch(
          apiUrl,
          {
            method: "PUT", // Change from POST to PUT
            headers: {
              "Authorization": `Bearer ${token}`,
              // NO Content-Type header for FormData - browser sets it automatically
            },
            body: formData
          }
        );

        const responseText = await response.text();

        if (!response.ok) {
          let errorMessage;
          try {
            const errorJson = JSON.parse(responseText);
            errorMessage = errorJson.message || errorJson.error || `HTTP ${response.status}`;
          } catch (e) {
            errorMessage = responseText || `HTTP ${response.status}`;
          }
          throw new Error(errorMessage);
        }

        // Parse response
        let result;
        try {
          result = JSON.parse(responseText);
          //console.log("✅ Success:", result);
        } catch (e) {
          //console.log("Response not JSON:", responseText);
          result = { message: responseText };
        }

        // Update UI
        setRows(prevRows =>
          prevRows.map(row =>
            row.key === "Address"
              ? { ...row, status: "Provided", isEditing: false }
              : row
          )
        );

        setEditingField(null);
        getVendorDetails(vendorId);

        alert("✅ Address updated successfully!");

      } catch (error) {
        console.error("❌ Error:", error);
        alert(`Error: ${error.message}`);
      } finally {
        hideLoader();
      }
    }
  };
  const handleCancelEdit = () => {
    // Exit edit mode for all rows
    setRows(prevRows => prevRows.map(r => ({ ...r, isEditing: false })));

    // Clear editing field (hides submit button)
    setEditingField(null);

    // Optionally: reload original data
    if (currentVendorId) {
      getVendorDetails(currentVendorId);
    }
  };
  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    setRows(prevRows => prevRows.map((row, i) => {
      if (i === index) {
        if (file) return { ...row, document: file, status: "Uploaded" };
        const placeholder = getServerDocPlaceholder(row.key, vendorDetails);
        const status = getStatusForDocumentField(row.key, vendorDetails);
        return { ...row, document: placeholder, status: status };
      }
      return row;
    }));
    if (fileInputRefs.current[index]) fileInputRefs.current[index].value = null;
  };

  const handleNestedFileChange = (e, rowIndex, fileFieldKeyInObject) => {
    const file = e.target.files[0] || null;
    setRows(prevRows => prevRows.map((row, i) => {
      if (i === rowIndex) {
        const updatedValue = { ...row.value, [fileFieldKeyInObject]: file };
        if (file && fileFieldKeyInObject === 'passbookFile') {
          updatedValue.passbookServerFileName = "";
        }
        return { ...row, value: updatedValue };
      }
      return row;
    }));
    if (e.target) e.target.value = null;
  };

  const handleUploadClick = (index) => fileInputRefs.current[index]?.click();

  const handleNestedUploadTrigger = (rowIndex, fileFieldKeyInObject) => {
    const refKey = `${rowIndex}_${fileFieldKeyInObject}`;
    if (nestedFileInputRefs.current[refKey]) {
      nestedFileInputRefs.current[refKey].click();
    }
  };

  const getValue = (key) => rows.find((r) => r.key === key)?.value;
  const getDocumentFile = (key) => { const row = rows.find((r) => r.key === key); return (row && row.document instanceof File) ? row.document : null; };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem("authToken");
    if (!vendorId) {
      alert("Vendor ID is missing. Please log in again.");
      setLoading(false);
      return;
    }
    try {
      showLoader();
      const addressValue = getValue("Address") || {};
      const bankDetailsValue = getValue("Bank Details") || {};

      let vendorData = {
        ownerName: getValue("Owner Name") || null,
        registeredName: getValue("Company Name") || null,
        businessPlan: getValue("Business Plan") || null,
        planType: getValue("Business Plan")
          ? getValue("Business Plan").toUpperCase()
          : null,
        type: getValue("Outlet Vertical")
          ? getValue("Outlet Vertical").toUpperCase()
          : null,
        vendorType: getValue("Vendor Type")
          ? getValue("Vendor Type").toUpperCase()
          : "Restaurant",
        businessVertical:
          getValue("Business Vertical") || "FOOD_AND_BEVERAGES",
        orderTypes:
          getValue("Order Type")?.length > 0
            ? getValue("Order Type")
            : ["DINE_IN"],
        websiteName: getValue("Website") || null,
        mobileNumber: getValue("Phone No") || null,
        email: getValue("Email ID") || null,
        doorNumber: addressValue.doorNo || null,
        addressLine: addressValue.street || null,
        city: addressValue.city || null,
        state: addressValue.state || null,
        pincode: addressValue.pincode
          ? parseInt(addressValue.pincode, 10)
          : null,
        landMark: addressValue.landmark || null,
        location: addressValue.location || null,
        aadharNumber: getValue("AADHAR CARD NO") || null,
        panCardNumber: getValue("PAN CARD NO") || null,
        gstNumber: getValue("GST No.") || null,
        tradeLicenseStartDate: getValue("Trade License")?.startDate || null,
        tradeLicenseEndDate: getValue("Trade License")?.endDate || null,
        fssaiStartDate: getValue("FSC License")?.startDate || null,
        fssaiEndDate: getValue("FSC License")?.endDate || null,
        labourStartDate: getValue("Labour License")?.startDate || null,
        labourEndDate: getValue("Labour License")?.endDate || null,
        fssaiLicenseNumber: getValue("FSC License")?.licenseNumber || null,
        tradeLicenseNumber: getValue("Trade License")?.licenseNumber || null,
        labourLicenseNumber: getValue("Labour License")?.licenseNumber || null,
        holderName: bankDetailsValue.holderName || null,
        accountNumber: bankDetailsValue.accountNumber || null,
        branchName: bankDetailsValue.branchName || null,
        ifscCode: bankDetailsValue.ifscCode || null,
        remarks: getValue("Remarks") || null,
        latitude: addressValue.latitude || selectedAddress?.latitude || 0,
        longitude: addressValue.longitude || selectedAddress?.longitude || 0,
        vendorId: vendorId,
      };

      const formDataToSend = new FormData();
      formDataToSend.append(
        "vendorData",
        new Blob([JSON.stringify(vendorData)], { type: "application/json" })
      );

      if (bankDetailsValue.passbookFile instanceof File) {
        formDataToSend.append("passbook", bankDetailsValue.passbookFile);
      }

      const docKeys = [
        { rowKey: "AADHAR CARD NO", formKey: "aadharPhoto", twoSided: true },
        { rowKey: "PAN CARD NO", formKey: "panCard" },
        { rowKey: "Register Documents", formKey: "registeredDocuments", twoSided: true },
        { rowKey: "Trade License", formKey: "tradeLicense" },
        { rowKey: "FSC License", formKey: "fssaiLicense" },
        { rowKey: "Labour License", formKey: "labourLicense" },
      ];

      docKeys.forEach(({ rowKey, formKey, twoSided }) => {
        const file = getDocumentFile(rowKey);
        if (file instanceof File) {
          if (twoSided) {
            formDataToSend.append(`${formKey}Front`, file);
            formDataToSend.append(`${formKey}Back`, file);
          } else {
            formDataToSend.append(formKey, file);
          }
        }
      });

      const apiUrl = `https://backend.maamaas.com/food/api/vendors/${vendorId}`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Backend error:", errorText);
        alert(`❌ Submission Failed:\n${errorText}`);
        throw new Error(`HTTP ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      // ✅ Set registration as complete after successful submission
      setIsRegistrationComplete(true);

      alert("✅ Vendor registered successfully!");
      window.location.reload();
    } catch (error) {
      console.error("❌ Submission error:", error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    const storedVendorId = localStorage.getItem("vendorId");
    if (storedVendorId) {
      setVendorId(storedVendorId);
    } else {
      console.warn("Vendor ID not found in localStorage.");
    }
  }, []);

  return (

    <>
      <style>
        {`
      /* Hide scrollbar on Chrome, Edge, Safari */
.hide-scrollbar::-webkit-scrollbar {
    display: none;
}

/* Hide scrollbar on Firefox */
@media(min-height:600px){

.hide-scrollbar {
    scrollbar-width: thin;
    }
  }

/* Hide scrollbar for Internet Explorer / old Edge */
.hide-scrollbar {
    -ms-overflow-style: none;
}



      `}
      </style>

      <div className="scrooltable hide-scrollbar" style={{ marginTop: "10px", padding: "0px" }}>
        <form onSubmit={handleSubmit}>
          <div
            ref={tableContainerRef}
            style={{
              maxHeight: window.innerHeight <= 600 ? "50vh" : window.innerHeight <= 650 ? "55vh" : "60vh",
              overflow: "auto",
              border: "1px solid rgba(221, 221, 221, 1)",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}
            className="hide-scrollbar"
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "0",
                tableLayout: "fixed"
              }}>
              <thead style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "#b15cd6"
              }}>
                <tr>
                  <th style={{ ...tableHeaderStyle, width: window.innerWidth < 1100 ? "120px" : "150px" }}>Field</th>
                  <th style={{ ...tableHeaderStyle, width: window.innerWidth < 1100 ? "300px" : "350px" }}>Value</th>
                  <th style={{ ...tableHeaderStyle, width: "150px" }}>Document</th>
                  <th style={{ ...tableHeaderStyle, width: "80px" }}>Status</th>
                  <th style={{ ...tableHeaderStyle, width: "80px" }}>Actions</th>
                </tr>
              </thead>
              <tbody style={{ overflowY: "auto" }}>
                {rows.map((row, index) => (
                  <tr key={row.key} style={index % 2 === 0 ? evenRowStyle : oddRowStyle}>
                    <td style={tableCellStyle}>
                      <strong>{row.key}</strong>
                    </td>

                    <td style={{
                      ...tableCellStyle,
                      textAlign: "left",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}>
                      {row.isEditing && row.key !== "Remarks" ? (
                        row.key.includes("License") ? (
                          (row.value?.startDate !== undefined || row.value?.endDate !== undefined || row.value?.licenseNumber !== undefined) ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                              <input
                                type="date"
                                value={row.value.startDate || ""}
                                onChange={(e) =>
                                  setRows((prevRows) =>
                                    prevRows.map((r, i) =>
                                      i === index ? { ...r, value: { ...r.value, startDate: e.target.value } } : r
                                    )
                                  )
                                }
                                style={inputStyle}
                                placeholder="Start Date"
                              />
                              <input
                                type="date"
                                value={row.value.endDate || ""}
                                onChange={(e) =>
                                  setRows((prevRows) =>
                                    prevRows.map((r, i) =>
                                      i === index ? { ...r, value: { ...r.value, endDate: e.target.value } } : r
                                    )
                                  )
                                }
                                style={inputStyle}
                                placeholder="End Date"
                              />
                              <input
                                type="text"
                                value={row.value.licenseNumber || ""}
                                onChange={(e) =>
                                  setRows((prevRows) =>
                                    prevRows.map((r, i) =>
                                      i === index ? { ...r, value: { ...r.value, licenseNumber: e.target.value } } : r
                                    )
                                  )
                                }
                                placeholder="License Number"
                                style={inputStyle}
                              />
                            </div>
                          ) : (
                            "Invalid license data structure"
                          )
                        ) : row.isMultiSelectDropdown ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            {(planType === "PREMIUM"
                              ? ["DINE_IN", "DELIVERY", "TAKEAWAY", "TABLE_DINE_IN"]
                              : ["DINE_IN", "TAKEAWAY"]
                            ).map((opt) => (
                              <label key={opt} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                <input
                                  type="checkbox"
                                  checked={row.value.includes(opt)}
                                  onChange={(e) => {
                                    const newValue = e.target.checked
                                      ? [...row.value, opt]
                                      : row.value.filter((v) => v !== opt);
                                    setRows((prevRows) =>
                                      prevRows.map((r, i) =>
                                        i === index ? { ...r, value: newValue } : r
                                      )
                                    );
                                  }}
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        ) : row.isDropdown ? (
                          <select
                            value={row.value || ""}
                            onChange={(e) =>
                              setRows((prevRows) =>
                                prevRows.map((r, i) =>
                                  i === index ? { ...r, value: e.target.value } : r
                                )
                              )
                            }
                            style={inputStyle}
                          >
                            <option value="">-- Select --</option>
                            {row.options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        ) : row.key === "Address" || row.key === "Bank Details" ? (
                          typeof row.value === "object" && row.value !== null ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                              {Object.entries(row.value).map(([k, v_val]) => {
                                if (k === "passbookFile" || k === "passbookServerFileName") return null;
                                const isLatLng = k === "latitude" || k === "longitude";

                                return (
                                  <input
                                    key={k}
                                    type="text"
                                    value={v_val || ""}
                                    onChange={(e) => {
                                      if (!isLatLng) {
                                        setRows((prevRows) =>
                                          prevRows.map((r, rowIndex) => {
                                            if (rowIndex === index) {
                                              const currentVal =
                                                typeof r.value === "object" && r.value !== null
                                                  ? r.value
                                                  : getRowConfigForKey(row.key).value;
                                              return {
                                                ...r,
                                                value: { ...currentVal, [k]: e.target.value },
                                              };
                                            }
                                            return r;
                                          })
                                        );
                                      }
                                    }}
                                    style={{
                                      ...inputStyle,
                                      backgroundColor: isLatLng ? "#f5f5f5" : "#fff",
                                    }}
                                    placeholder={k
                                      .replace(/([A-Z])/g, " $1")
                                      .trim()
                                      .replace(/^\w/, (c) => c.toUpperCase())}
                                    readOnly={isLatLng}
                                  />
                                );
                              })}

                              {row.key === "Address" && (
                                <button
                                  type="button"
                                  onClick={() => setShowMap(true)}
                                  style={locationButtonStyle}
                                >
                                  📍 Add Location
                                </button>
                              )}
                            </div>
                          ) : (
                            <input type="text" value="Invalid data structure" disabled style={inputStyle} />
                          )
                        ) : (
                          <input
                            type={row.key === "Phone No" ? "tel" : "text"}
                            value={row.value || ""}
                            onChange={(e) =>
                              setRows((prevRows) =>
                                prevRows.map((r, i) =>
                                  i === index ? { ...r, value: e.target.value } : r
                                )
                              )
                            }
                            style={inputStyle}
                            placeholder={`Enter ${row.key}`}
                          />
                        )
                      ) : (
                        row.key === "Address" || row.key === "Bank Details" ? (
                          typeof row.value === "object" &&
                            row.value !== null &&
                            Object.values(row.value).some(
                              (val) =>
                                (typeof val === "string" && val.trim() !== "") ||
                                (typeof val === "object" && val !== null)
                            ) ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                              {Object.entries(row.value).map(([k, v_val]) => {
                                if (k === "passbookFile" || k === "passbookServerFileName") return null;
                                if (
                                  v_val &&
                                  (typeof v_val === "string" || typeof v_val === "number")
                                ) {
                                  return (
                                    <div key={k} style={{ fontSize: "0.9em" }}>
                                      <strong style={{ textTransform: "capitalize" }}>
                                        {k.replace(/([A-Z])/g, " $1").trim()}:
                                      </strong>{" "}
                                      {String(v_val)}
                                    </div>
                                  );
                                }
                                return null;
                              })}

                              {row.key === "Address" && (
                                <button
                                  type="button"
                                  onClick={() => setShowMap(true)}
                                  style={locationButtonStyle}
                                >
                                  📍 Add Location
                                </button>
                              )}
                            </div>
                          ) : (
                            <em>Not specified</em>
                          )
                        ) : row.key.includes("License") ? (
                          row.value && typeof row.value === "object" ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                              <div><strong>Start:</strong> {row.value.startDate || "N/A"}</div>
                              <div><strong>End:</strong> {row.value.endDate || "N/A"}</div>
                              <div><strong>License Number:</strong> {row.value.licenseNumber || "N/A"}</div>
                            </div>
                          ) : (
                            <em>Not specified</em>
                          )
                        ) : (
                          <div style={{ wordBreak: "break-word" }}>
                            {row.value || <em>Not specified</em>}
                          </div>
                        )
                      )}
                    </td>

                    <td style={tableCellStyle}>
                      {row.isUpload ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
                          <button
                            type="button"
                            style={buttonStyle}
                            onClick={() => handleUploadClick(index)}
                            disabled={isRegistrationComplete && row.key !== "Address"}
                          >
                            {row.document
                              ? row.document instanceof File
                                ? "Change File"
                                : "Replace Server File"
                              : "Upload File"}
                          </button>
                          <input
                            type="file"
                            style={{ display: "none" }}
                            ref={(el) => (fileInputRefs.current[index] = el)}
                            onChange={(e) => handleFileChange(e, index)}
                          />
                          <div style={fileInfoStyle}>
                            {row.document?.name ||
                              (row.document &&
                                typeof row.document === "object" &&
                                !row.document.name &&
                                row.document.name !== ""
                                ? "Server file present"
                                : "")}
                          </div>
                        </div>
                      ) : row.key === "Bank Details" ? (
                        (() => {
                          let passbookDisplayStatus;
                          let statusColor = "orange";

                          if (row.value.passbookFile) {
                            passbookDisplayStatus = "File Selected";
                            statusColor = "#007bff";
                          } else {
                            passbookDisplayStatus = getStatusForDocumentField(
                              "Passbook",
                              vendorDetails
                            );
                            if (["Verified", "Provided"].includes(passbookDisplayStatus)) {
                              statusColor = "green";
                            }
                          }
                          return (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
                              {row.isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    style={buttonStyle}
                                    onClick={() =>
                                      handleNestedUploadTrigger(index, "passbookFile")
                                    }
                                  >
                                    {row.value.passbookFile
                                      ? "Change Passbook"
                                      : row.value.passbookServerFileName
                                        ? "Replace Server Passbook"
                                        : "Upload Passbook"}
                                  </button>
                                  <input
                                    type="file"
                                    style={{ display: "none" }}
                                    ref={(el) =>
                                      (nestedFileInputRefs.current[`${index}_passbookFile`] = el)
                                    }
                                    onChange={(e) =>
                                      handleNestedFileChange(e, index, "passbookFile")
                                    }
                                    accept="image/*,.pdf,.doc,.docx"
                                  />
                                  <div style={fileInfoStyle}>
                                    {row.value.passbookFile?.name ||
                                      row.value.passbookServerFileName || (
                                        <em>No passbook selected</em>
                                      )}
                                  </div>
                                </>
                              ) : (
                                <div style={fileInfoStyle}>
                                  {row.value.passbookServerFileName || (
                                    <em>No passbook uploaded</em>
                                  )}
                                </div>
                              )}
                              <div style={{ ...fileInfoStyle, color: statusColor }}>
                                File Status: {passbookDisplayStatus}
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        "-"
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      <span
                        style={{
                          color: ["Verified", "Uploaded", "Submitted", "Provided"].includes(
                            row.status
                          )
                            ? "green"
                            : ["Invalid", "Error", "Rejected", "Not Uploaded", "Not Verified", "Pending"].includes(
                              row.status
                            )
                              ? "orange"
                              : "grey",
                          fontWeight: "bold",
                          fontSize: "0.9em"
                        }}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      {row.key === "Remarks" ? (
                        <span>-</span>
                      ) : row.isEditing ? (
                        <button
                          type="button"
                          style={saveButtonStyle}
                          onClick={() => handleSave(index)}
                        >
                          Save
                        </button>
                      ) : (
                        // ✅ Modified edit button logic
                        isFieldEditable(row.key) ? (
                          <button
                            type="button"
                            style={editButtonStyle}
                            onClick={() => handleEdit(index)}
                          >
                            Edit
                          </button>
                        ) : (
                          <button
                            type="button"
                            style={{ ...editButtonStyle, opacity: 0.5, cursor: "not-allowed" }}
                            disabled
                          >
                            Edit
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Show this submit button only when editing Address field after registration */}
            {isRegistrationComplete && editingField === "Address" && (
              <div style={{
                textAlign: "center",
                marginTop: "20px",
                padding: "15px",
                borderRadius: "8px",
              }}>


                <div style={{ display: "flex", justifyContent: "center", gap: "15px" }}>
                  <button
                    type="button"
                    onClick={handleSubmitFieldUpdate}
                    style={{
                      backgroundColor: "#28a745",
                      color: "white",
                      padding: "10px 25px",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      minWidth: "150px"
                    }}
                  >
                    Submit Address
                  </button>

                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    style={{
                      backgroundColor: "#6c757d",
                      color: "white",
                      padding: "10px 25px",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      minWidth: "150px"
                    }}
                  >
                    Cancel
                  </button>
                </div>


              </div>
            )}
          </div>
          {/* Keep this for initial registration */}
          {(!currentVendorId || !vendorDetails) && (
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button
                type="submit"
                style={{ ...submitAllButtonStyle, opacity: loading ? 0.7 : 1 }}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit & Next"}
              </button>
            </div>
          )}
        </form>
        {loading && <div style={{ marginTop: '10px', textAlign: 'center', fontWeight: 'bold' }}>Processing...</div>}
        {showMap && (
          <Modal onClose={() => setShowMap(false)}>
            <h3>Select Location</h3>
            <LocationPicker
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
            />
            <button
              onClick={handleConfirmLocation}
              style={locationButtonStyle}
            >
              Confirm Location
            </button>
          </Modal>
        )}
      </div>


    </>

  );
};

// Styles
const tableHeaderStyle = {
  border: "1px solid #ccc",
  padding: "12px",
  backgroundColor: "#b15cd6",
  color: "white",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "14px"
};

const tableCellStyle = {
  border: "1px solid #ddd",
  padding: "10px",
  verticalAlign: "top",
  textAlign: "center",
  fontSize: window.innerWidth < 1100 ? "12px" : "13px",
};

const evenRowStyle = { backgroundColor: "#f9f9f9" };
const oddRowStyle = { backgroundColor: "#ffffff" };

const inputStyle = {
  width: "100%",
  border: "1px solid #ccc",
  padding: "8px",
  borderRadius: "4px",
  fontSize: "13px"
};

const buttonStyle = {
  backgroundColor: "#5bc0de",
  color: "white",
  padding: "6px 12px",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "bold"
};

const editButtonStyle = { ...buttonStyle, backgroundColor: "#f0ad4e" };
const saveButtonStyle = { ...buttonStyle, backgroundColor: "#5cb85c" };

const submitAllButtonStyle = {
  backgroundColor: "#008CBA",
  color: "white",
  padding: "12px 24px",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "bold"
};

const locationButtonStyle = {
  backgroundColor: "#4CAF50",
  color: "#fff",
  padding: "8px 16px",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "bold",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  width:window.innerWidth<1600 ? "50%" : "30%",
  whiteSpace:"nowrap",
};

const fileInfoStyle = {
  marginTop: "5px",
  fontSize: "0.8em",
  wordBreak: "break-all",
  textAlign: "center"
};

export default Registration;



